import {
	ConfirmationDialog,
	ContextMenu,
	ContextMenuItem,
	ContextMenuLinkItem,
	EntityCreateDrawerButton,
	IDataListColumn,
	IconButton,
	Id,
	Loading,
	PaginatedAutoDataList,
	PaginatedListFetcherConfig,
	laravelPaginatedListFetchConfig,
	useEditDrawer,
	useLaravelCreateEntity,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { ContactFilterApplyDrawer, ContactFilterBar } from "../contactFilter";
import { useGlobalContactView } from "../contactViews/global/GlobalContactViewProvider";
import { birthdayWidgetService } from "../dashboard/widgets/birthdays/service";
import { clippingsService } from "../shared/api/clippingsService";
import { companyService } from "../shared/api/companyService";
import { contactService } from "../shared/api/contactService";
import { contactUpdateInquiryService } from "../shared/api/contactUpdateInquiryService";
import { useDataFieldsQuery } from "../shared/api/dataFieldService";
import { archivedEventService } from "../shared/api/event/archivedEventService";
import { eventService } from "../shared/api/event/eventService";
import { TenantArchiveDrawer } from "../shared/tenantArchive/TenantArchiveDrawer";
import { useArchiveByTenantHandler } from "../shared/tenantArchive/useRestoreByTenantHandler";
import { ContactCreateRequest, IContactIndexResource } from "../shared/types";
import { ContactBulkEditAction } from "./ContactBulkEditAction";
import { ContactExportButton } from "./ContactExportButton";
import { CreateContactDrawer } from "./create/CreateContactDrawer";
import { useContactListUtils } from "./useContactListUtils";
import { ContactUpdateDrawer } from "./view/ContactUpdateDrawer";

export const ContactList: FC = () => {
	const { t } = useTranslation();
	const { globalContactView, isLoading: contactViewLoading } = useGlobalContactView();
	const { data: dataFields, isLoading } = useDataFieldsQuery();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { columns } = useContactListUtils(globalContactView);
	const editDrawerProps = useEditDrawer();
	const applyFilterDrawerProps = useEditDrawer();
	const [searchParams, setSearchParams] = useSearchParams();
	const activeContactFilterId = useMemo(() => {
		const id = searchParams.get("contactFilterId");
		return id ? +id : null;
	}, [searchParams]);

	const queryKey = useMemo(() => {
		if (globalContactView?.id) {
			return [...contactService.listKey, String(activeContactFilterId), String(globalContactView.id)];
		}
		return [...contactService.listKey, String(activeContactFilterId)];
	}, [activeContactFilterId, globalContactView]);

	const initialSortingAttribute =
		globalContactView?.contactAttributes.find((attribute) => attribute.entityType === "lastName") ??
		globalContactView?.contactAttributes.find((attribute) => attribute.entityType === "createdAt");
	const {
		clickHandler: archiveClickHandler,
		drawerProps,
		confirmationProps,
	} = useArchiveByTenantHandler(contactService, () => {
		void queryClient.invalidateQueries({ queryKey: companyService.indexKey });
		void queryClient.invalidateQueries({ queryKey: birthdayWidgetService.key });
	});
	const onApplyContactFilter = useCallback(
		(id: Id | null) => {
			if (id) {
				setSearchParams((prev) => {
					prev.set("contactFilterId", `${id}`);
					return prev;
				});
			} else {
				setSearchParams((prev) => {
					prev.delete("contactFilterId");
					return prev;
				});
			}
			void queryClient.invalidateQueries({ queryKey: contactService.listKey });
			void queryClient.invalidateQueries({ queryKey: contactUpdateInquiryService.indexKey });
		},
		[setSearchParams, queryClient],
	);

	const createContact = useLaravelCreateEntity(
		contactService,
		{ entityName: t("Contact"), message: t("Contact was saved.") },
		[clippingsService.indexKey, archivedEventService.indexKey, eventService.indexKey],
	);

	const handleCreateContact = useCallback(
		async (request: ContactCreateRequest) => {
			const response = await createContact(request);

			if (response !== false) {
				void queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes("contacts") });
			}
			return response !== false;
		},
		[createContact, queryClient],
	);

	const dataListColumns: IDataListColumn<IContactIndexResource>[] = useMemo(
		() => [
			...columns,
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem
							title={t("Edit")}
							label={t("Edit")}
							action={() => editDrawerProps.handleOpen(id)}
							icon="edit"
							shortcut="primary"
						/>
						<ContextMenuLinkItem to={`${id}`} title={t("View")} label={t("View")} icon="eye" shortcut="primary" />
						<ContextMenuItem
							action={() => archiveClickHandler(id)}
							title={t("Archive")}
							label={t("Archive")}
							icon="archive_2"
						/>
					</ContextMenu>
				),
			},
		],
		[archiveClickHandler, columns, editDrawerProps, t],
	);

	const openContactFilterDrawer = useCallback(() => {
		applyFilterDrawerProps.handleOpen(activeContactFilterId || "new");
	}, [activeContactFilterId, applyFilterDrawerProps]);

	if (isLoading || contactViewLoading) {
		return <Loading />;
	}

	return (
		<>
			<PaginatedAutoDataList
				name="contacts"
				empty={t("No contact available.")}
				headerChildren={
					<ContactFilterBar
						contactFilterId={activeContactFilterId}
						onApply={onApplyContactFilter}
						onClick={openContactFilterDrawer}
					/>
				}
				heading={t("Contacts")}
				columns={dataListColumns}
				listFetchConfig={laravelPaginatedListFetchConfig(
					queryKey,
					(config: PaginatedListFetcherConfig<IContactIndexResource>) =>
						contactService.indexActiveWithContactFilter(config, activeContactFilterId, globalContactView?.id || null),
				)}
				headerActions={
					<>
						<EntityCreateDrawerButton
							renderDrawer={(drawerProps) => <CreateContactDrawer {...drawerProps} onSubmit={handleCreateContact} />}
						/>
						<IconButton size="small" title={t("Contact filter")} icon="filter" onClick={openContactFilterDrawer} />
						<ContactExportButton isArchived={false} filterId={activeContactFilterId} viewId={globalContactView?.id} />
					</>
				}
				initialSearch={{
					search: "",
					searchFields: [
						{ name: "firstName", label: t("First name") },
						{ name: "lastName", label: t("Last name") },
						{ name: "email", label: t("Email") },
						{ name: "phone", label: t("Phone") },
						{ name: "company", label: t("Company") },
					],
				}}
				onRowClick={({ id }) => navigate(activeContactFilterId ? `/contacts/${id}` : `${id}`)}
				bulkEditActions={<ContactBulkEditAction contactFilterId={activeContactFilterId} />}
				enableBulkEdit={true}
				initialSorting={
					initialSortingAttribute ? { column: initialSortingAttribute.id as any, direction: "asc" } : null
				}
			/>
			{applyFilterDrawerProps.editEntityId != null && (
				<ContactFilterApplyDrawer
					{...applyFilterDrawerProps}
					contactFilterId={activeContactFilterId}
					onApply={onApplyContactFilter}
				/>
			)}
			{!!editDrawerProps.editEntityId && globalContactView && (
				<ContactUpdateDrawer
					{...editDrawerProps}
					id={editDrawerProps.editEntityId}
					dataFields={dataFields}
					contactViewId={globalContactView.id}
				/>
			)}
			<TenantArchiveDrawer {...drawerProps} title={t("Archive")} type="archive" service={contactService} />
			<ConfirmationDialog
				{...confirmationProps}
				heading={t("Archive")}
				labels={{ close: t("Close"), confirm: t("Confirm") }}
			>
				{t("Are you sure you want to archive this contact?")}
			</ConfirmationDialog>
		</>
	);
};
